'use client';

import type { Cart, CartItem, Product, ProductVariant } from 'lib/shopify/types';
import React, { createContext, use, useContext, useMemo, useOptimistic } from 'react';
import { sendGAEvent } from '@next/third-parties/google';
import Clarity from '@microsoft/clarity';

type UpdateType = 'plus' | 'minus' | 'delete';

type CartAction =
  | {
      type: 'UPDATE_ITEM';
      payload: {
        merchandiseId: string;
        updateType: UpdateType;
        attributes?: { key: string; value: string }[];
      };
    }
  | {
      type: 'ADD_ITEM';
      payload: {
        variant: ProductVariant;
        product: Product;
        attributes?: { key: string; value: string }[];
      };
    };

type CartContextType = {
  cart: Cart | undefined;
  updateCartItem: (
    merchandiseId: string,
    updateType: UpdateType,
    attributes?: { key: string; value: string }[]
  ) => void;
  addCartItem: (
    variant: ProductVariant,
    product: Product,
    attributes?: { key: string; value: string }[]
  ) => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

function calculateItemCost(quantity: number, price: string): string {
  return (Number(price) * quantity).toString();
}

function updateCartItem(
  item: CartItem,
  updateType: UpdateType,
  attributes?: { key: string; value: string }[]
): CartItem | null {
  if (updateType === 'delete') return null;

  const newQuantity = updateType === 'plus' ? item.quantity + 1 : item.quantity - 1;
  if (newQuantity <= 0) return null; // Ensure quantity doesn't go below 1

  const singleItemAmount = Number(item.cost.totalAmount.amount) / item.quantity;
  const newTotalAmount = calculateItemCost(newQuantity, singleItemAmount.toString());

  return {
    ...item,
    quantity: newQuantity,
    cost: {
      ...item.cost,
      totalAmount: {
        ...item.cost.totalAmount,
        amount: newTotalAmount
      }
    }
  };
}

function createOrUpdateCartItem(
  existingItem: CartItem | undefined,
  variant: ProductVariant,
  product: Product,
  attributes?: { key: string; value: string }[]
): CartItem {
  const quantity = existingItem ? existingItem.quantity + 1 : 1;
  const totalAmount = calculateItemCost(quantity, variant.price.amount);

  const mergedAttributes = existingItem
    ? [...(existingItem.attributes ?? []), ...(attributes ?? [])]
    : attributes || [];

  return {
    id: existingItem?.id,
    quantity,
    cost: {
      totalAmount: {
        amount: totalAmount,
        currencyCode: variant.price.currencyCode
      }
    },
    merchandise: {
      id: variant.id,
      title: variant.title,
      selectedOptions: variant.selectedOptions,
      product: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        featuredImage: product.featuredImage
      }
    },
    attributes: mergedAttributes
  };
}

function updateCartTotals(lines: CartItem[]): Pick<Cart, 'totalQuantity' | 'cost'> {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce((sum, item) => sum + Number(item.cost.totalAmount.amount), 0);
  const currencyCode = lines[0]?.cost.totalAmount.currencyCode ?? 'USD';

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode }
    }
  };
}

function createEmptyCart(): Cart {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'EUR' },
      totalAmount: { amount: '0', currencyCode: 'EUR' },
      totalTaxAmount: { amount: '0', currencyCode: 'EUR' }
    }
  };
}

function areAttributesEqual(
  attributes1: { key: string; value: string }[] | undefined,
  attributes2: { key: string; value: string }[] | undefined
): boolean {
  if (!attributes1 || !attributes2) return attributes1 === attributes2;
  if (attributes1.length !== attributes2.length) return false;

  return attributes1.every((attr1) =>
    attributes2.some((attr2) => attr1.key === attr2.key && attr1.value === attr2.value)
  );
}

function cartReducer(state: Cart | undefined, action: CartAction): Cart {
  const currentCart = state || createEmptyCart();

  switch (action.type) {
    case 'UPDATE_ITEM': {
      const { merchandiseId, updateType, attributes } = action.payload;
      const updatedLines = currentCart.lines
        .map((item) =>
          item.merchandise.id === merchandiseId && areAttributesEqual(item.attributes, attributes)
            ? updateCartItem(item, updateType)
            : item
        )
        .filter((item): item is CartItem => item !== null); // Ensure null items are filtered out

      if (updatedLines.length === 0) {
        return {
          ...currentCart,
          lines: [],
          totalQuantity: 0,
          cost: {
            ...currentCart.cost,
            totalAmount: { ...currentCart.cost.totalAmount, amount: '0' }
          }
        };
      }

      return { ...currentCart, ...updateCartTotals(updatedLines), lines: updatedLines };
    }
    case 'ADD_ITEM': {
      const { variant, product, attributes } = action.payload;
      const existingItem = currentCart.lines.find(
        (item) =>
          item.merchandise.id === variant.id && areAttributesEqual(item.attributes, attributes)
      );
      const updatedItem = createOrUpdateCartItem(existingItem, variant, product, attributes);

      const updatedLines = existingItem
        ? currentCart.lines.map((item) =>
            item.merchandise.id === variant.id && areAttributesEqual(item.attributes, attributes)
              ? updatedItem
              : item
          )
        : [...currentCart.lines, updatedItem];

      return { ...currentCart, ...updateCartTotals(updatedLines), lines: updatedLines };
    }
    default:
      return currentCart;
  }
}

export function CartProvider({
  children,
  cartPromise
}: {
  children: React.ReactNode;
  cartPromise: Promise<Cart | undefined>;
}) {
  const initialCart = use(cartPromise);
  const [optimisticCart, updateOptimisticCart] = useOptimistic(initialCart, cartReducer);

  const updateCartItem = (
    merchandiseId: string,
    updateType: UpdateType,
    attributes?: { key: string; value: string }[]
  ) => {
    updateOptimisticCart({
      type: 'UPDATE_ITEM',
      payload: { merchandiseId, updateType, attributes }
    });

    if (process.env.VERCEL_ENV === 'production') {
      try {
        if (
          updateType == 'delete' ||
          (updateType == 'minus' && optimisticCart?.totalQuantity == 1)
        ) {
          sendGAEvent('event', 'remove_from_cart', {
            currency: optimisticCart?.cost.totalAmount.currencyCode,
            value: Number(optimisticCart?.cost.totalAmount.amount),
            items: [
              {
                item_id: merchandiseId,
                price: Number(optimisticCart?.cost.totalAmount.amount),
                quantity: 1
              }
            ]
          });
          Clarity.event('remove_from_cart');
        } else if (updateType == 'plus') {
          sendGAEvent('event', 'add_to_cart', {
            currency: optimisticCart?.cost.totalAmount.currencyCode,
            value: Number(optimisticCart?.cost.totalAmount.amount),
            items: [
              {
                item_id: merchandiseId,
                price: Number(optimisticCart?.cost.totalAmount.amount),
                quantity: 1
              }
            ]
          });
          Clarity.event('add_to_cart');
        }
      } catch (error) {
        console.warn(error);
      }
    }
  };

  const addCartItem = (
    variant: ProductVariant,
    product: Product,
    attributes?: { key: string; value: string }[]
  ) => {
    updateOptimisticCart({ type: 'ADD_ITEM', payload: { variant, product, attributes } });

    if (process.env.VERCEL_ENV === 'production') {
      try {
        sendGAEvent('event', 'add_to_cart', {
          currency: variant.price.currencyCode,
          value: Number(variant.price.amount),
          items: [
            {
              item_id: variant.id,
              item_name: product.title,
              item_variant: variant.title,
              price: Number(variant.price.amount),
              quantity: 1
            }
          ]
        });
        Clarity.event('add_to_cart');
      } catch (error) {
        console.warn(error);
      }
    }
  };

  const value = useMemo(
    () => ({
      cart: optimisticCart,
      updateCartItem,
      addCartItem
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optimisticCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
